import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Mail, Lock, User as UserIcon, ArrowLeft, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { track, trackServer, getUtmParams } from '../lib/analytics';

// Read API base the same way the rest of the app does. Using a same-
// origin /api path keeps the cookie attached without CORS preflights.
const API = '/api/auth/email';

// formatApiErrorDetail - FastAPI returns a string on business errors
// but an Array<{msg,...}> on 422 validation. Rendering the array
// directly crashes React, so always normalise to a string.
const formatApiErrorDetail = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
    }
    if (typeof detail.msg === 'string') return detail.msg;
    return String(detail);
};

async function postJson(path, body) {
    const res = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    let payload = null;
    try { payload = await res.json(); } catch (_) { /* ignore */ }
    if (!res.ok) {
        const msg = formatApiErrorDetail(payload?.detail);
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }
    return payload;
}

// EmailAuthSheet - bottom-sheet on mobile, centred modal on desktop
// (Dialog handles both). Holds 3 internal modes: register, verify,
// login + a forgot-password sub-step. Reusable from anywhere - the
// landing page passes `onOpenChange` and (optionally) an initial mode.
//
// Why a single component (not 3 separate pages): the entire flow
// completes in <30 seconds for most users (enter email → code →
// done), so keeping it in one focused surface beats multi-route
// navigation. The user never loses their place.
export const EmailAuthSheet = ({ open, onOpenChange, initialMode = 'register', onGoogleSignIn }) => {
    const { checkAuth, login, user } = useAuth();
    // Modes: 'register' | 'login' | 'verify' | 'forgot' | 'forgot_sent'
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const emailRef = useRef(null);

    // If auth resolves while the sheet is open (e.g. TWA native
    // Google sign-in returns an id_token and AuthContext sets `user`
    // seconds later), close the sheet immediately. Without this, the
    // sheet briefly re-flashes on top of the bookmark page in the
    // tick between the "Signing you in…" veil dismissing and the
    // parent's navigation firing. (Reported on TWA Aug 26 2026.)
    useEffect(() => {
        if (open && user) {
            onOpenChange?.(false);
        }
    }, [user, open, onOpenChange]);

    // Reset everything when the sheet opens fresh (avoids stale state
    // from a previous abandoned flow). We don't reset on close - that
    // would flicker stale UI during the close animation.
    useEffect(() => {
        if (open) {
            setMode(initialMode);
            setError('');
            // Don't clear email/password - if the user typed them, accept
            // them as they reopen the sheet (e.g. after a network blip).
        }
    }, [open, initialMode]);

    // Resend code countdown - server throttles to 60s/email anyway,
    // mirroring that in the UI is courtesy. Tick once a second.
    useEffect(() => {
        if (resendCooldown <= 0) return undefined;
        const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    // Intentionally no auto-focus on any field. The primary CTA is
    // "Continue with Google" — auto-focusing an input would raise the
    // keyboard on mobile and push the Google button off-screen, biasing
    // users toward the email path and away from the higher-converting
    // OAuth path. (Reported via video Aug 24 2026 — keyboard was
    // opening on the Name field the moment the sheet appeared.)
    // If the user actively taps an email input, the browser focuses it
    // normally.

    const guardedSubmit = async (fn) => {
        if (busy) return;
        setBusy(true);
        setError('');
        try { await fn(); } catch (e) { setError(e.message || 'Something went wrong'); }
        finally { setBusy(false); }
    };

    const handleRegister = (e) => {
        e?.preventDefault();
        guardedSubmit(async () => {
            await postJson('/register', { email: email.trim(), password, name: name.trim() });
            setMode('verify');
            setResendCooldown(60);
            toast.success('Check your inbox for the 6-digit code');
        });
    };

    const handleLogin = (e) => {
        e?.preventDefault();
        guardedSubmit(async () => {
            const user = await postJson('/login', { email: email.trim(), password });
            toast.success(`Welcome back, ${user.name?.split(' ')[0] || 'there'}`);
            track('login', { method: 'email' });
            await checkAuth({ silent: false, maxRetries: 1 });
            onOpenChange(false);
        });
    };

    const handleVerify = (e) => {
        e?.preventDefault();
        guardedSubmit(async () => {
            // Capture the /verify response so we can trust the
            // backend-derived `is_new_user` flag. The endpoint returns
            // `is_new_user: false` when this verify is actually an
            // account-merge flow (Google user adding a password) —
            // firing `sign_up` in that case would double-count the
            // signup in GA4.
            const verified = await postJson('/verify', { email: email.trim(), code: code.trim() });
            toast.success('Account verified - welcome to BrainSync');
            if (verified?.is_new_user) {
                // Server-side mirror so ad-blocked users still land in
                // the funnel. Attach UTM attribution too.
                trackServer('sign_up', { method: 'email', ...getUtmParams() });
            } else {
                track('login', { method: 'email' });
            }
            await checkAuth({ silent: false, maxRetries: 1 });
            onOpenChange(false);
        });
    };

    const handleResend = () => {
        if (resendCooldown > 0) return;
        guardedSubmit(async () => {
            await postJson('/resend-code', { email: email.trim() });
            setResendCooldown(60);
            toast.success('Another code is on the way');
        });
    };

    const handleForgot = (e) => {
        e?.preventDefault();
        guardedSubmit(async () => {
            await postJson('/forgot', { email: email.trim() });
            setMode('forgot_sent');
        });
    };

    // ── Render ────────────────────────────────────────────────────
    const isRegister = mode === 'register';
    const isLogin = mode === 'login';
    const isVerify = mode === 'verify';
    const isForgot = mode === 'forgot';
    const isForgotSent = mode === 'forgot_sent';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-md rounded-2xl p-0 overflow-hidden"
                data-testid="email-auth-sheet"
            >
                <div className="p-6 sm:p-7">
                    {/* Header - back button on inner steps, dynamic title */}
                    <div className="flex items-center gap-2 mb-5">
                        {(isVerify || isForgot || isForgotSent) && (
                            <button
                                type="button"
                                onClick={() => { setMode(isVerify ? 'register' : 'login'); setError(''); }}
                                className="w-8 h-8 -ml-1 rounded-full flex items-center justify-center hover:bg-zinc-100"
                                aria-label="Back"
                                data-testid="email-auth-back-btn"
                            >
                                <ArrowLeft className="w-4 h-4 text-zinc-600" />
                            </button>
                        )}
                        <DialogTitle className="text-[19px] font-bold text-zinc-900 tracking-tight">
                            {isRegister && 'Create your account'}
                            {isLogin && 'Welcome back'}
                            {isVerify && 'Check your email'}
                            {isForgot && 'Reset password'}
                            {isForgotSent && 'Check your inbox'}
                        </DialogTitle>
                    </div>

                    {/* Subtitle */}
                    <p className="text-[13.5px] text-zinc-500 -mt-3 mb-5 leading-snug">
                        {isRegister && "We'll send a quick 6-digit code to confirm it's you."}
                        {isLogin && 'Sign in with the email + password you registered with.'}
                        {isVerify && `We sent a 6-digit code to `}
                        {isVerify && <span className="font-semibold text-zinc-700">{email}</span>}
                        {isForgot && "Enter your email and we'll send you a reset link."}
                        {isForgotSent && "If an account exists for that email, a reset link is on its way. It expires in 1 hour."}
                    </p>

                    {/* ── REGISTER ── */}
                    {isRegister && (
                        <form onSubmit={handleRegister} className="space-y-3" data-testid="email-auth-register-form">
                            <GoogleAuthButton onGoogleSignIn={onGoogleSignIn ?? login} />
                            <OrDivider />
                            <FieldIcon icon={<UserIcon className="w-4 h-4" />}>
                                <Input
                                    ref={emailRef}
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="name"
                                    required
                                    data-testid="email-auth-name-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            <FieldIcon icon={<Mail className="w-4 h-4" />}>
                                <Input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    data-testid="email-auth-email-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            <FieldIcon icon={<Lock className="w-4 h-4" />}>
                                <Input
                                    type="password"
                                    placeholder="Password (8+ characters)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    data-testid="email-auth-password-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            {error && <ErrorText message={error} />}
                            <Button
                                type="submit"
                                disabled={busy || !email || !password || !name || password.length < 8}
                                className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold mt-1"
                                data-testid="email-auth-register-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send me a code'}
                            </Button>
                            <p className="text-center text-[12.5px] text-zinc-500 pt-1">
                                Already have an account?{' '}
                                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-emerald-700 font-semibold hover:underline" data-testid="email-auth-switch-to-login">
                                    Sign in
                                </button>
                            </p>
                        </form>
                    )}

                    {/* ── LOGIN ── */}
                    {isLogin && (
                        <form onSubmit={handleLogin} className="space-y-3" data-testid="email-auth-login-form">
                            <GoogleAuthButton onGoogleSignIn={onGoogleSignIn ?? login} />
                            <OrDivider />
                            <FieldIcon icon={<Mail className="w-4 h-4" />}>
                                <Input
                                    ref={emailRef}
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    data-testid="email-auth-email-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            <FieldIcon icon={<Lock className="w-4 h-4" />}>
                                <Input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    data-testid="email-auth-password-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            {error && <ErrorText message={error} />}
                            <Button
                                type="submit"
                                disabled={busy || !email || !password}
                                className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold mt-1"
                                data-testid="email-auth-login-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
                            </Button>
                            <div className="flex items-center justify-between pt-1 text-[12.5px]">
                                <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-zinc-500 hover:text-zinc-800" data-testid="email-auth-forgot-link">
                                    Forgot password?
                                </button>
                                <button type="button" onClick={() => { setMode('register'); setError(''); }} className="text-emerald-700 font-semibold hover:underline" data-testid="email-auth-switch-to-register">
                                    Create account
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── VERIFY ── */}
                    {isVerify && (
                        <form onSubmit={handleVerify} className="space-y-3" data-testid="email-auth-verify-form">
                            <Input
                                ref={emailRef}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                placeholder="123456"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                required
                                data-testid="email-auth-code-input"
                                className="h-14 text-center text-2xl font-mono tracking-[0.5em] rounded-xl"
                                autoComplete="one-time-code"
                            />
                            {error && <ErrorText message={error} />}
                            <Button
                                type="submit"
                                disabled={busy || code.length !== 6}
                                className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                data-testid="email-auth-verify-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & continue'}
                            </Button>
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={busy || resendCooldown > 0}
                                className="block mx-auto text-[12.5px] text-zinc-500 hover:text-zinc-800 disabled:opacity-50 pt-1"
                                data-testid="email-auth-resend-btn"
                            >
                                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                            </button>
                        </form>
                    )}

                    {/* ── FORGOT ── */}
                    {isForgot && (
                        <form onSubmit={handleForgot} className="space-y-3" data-testid="email-auth-forgot-form">
                            <FieldIcon icon={<Mail className="w-4 h-4" />}>
                                <Input
                                    ref={emailRef}
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    data-testid="email-auth-email-input"
                                    className="pl-10 h-11 rounded-xl"
                                />
                            </FieldIcon>
                            {error && <ErrorText message={error} />}
                            <Button
                                type="submit"
                                disabled={busy || !email}
                                className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                data-testid="email-auth-forgot-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
                            </Button>
                        </form>
                    )}

                    {/* ── FORGOT SENT ── */}
                    {isForgotSent && (
                        <div className="text-center py-4" data-testid="email-auth-forgot-sent">
                            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                                <KeyRound className="w-6 h-6 text-emerald-700" />
                            </div>
                            <Button
                                type="button"
                                onClick={() => { setMode('login'); setError(''); }}
                                className="rounded-full bg-zinc-900 text-white px-6 h-10 mt-3"
                                data-testid="email-auth-forgot-back-to-login"
                            >
                                Back to sign in
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// GoogleAuthButton - primary Google OAuth CTA rendered at the top of
// the register + login forms. Delegates to the same `login()` handler
// AuthContext exposes, which auto-selects the best flow (native TWA
// bridge → web OAuth fallback → deep-link into installed TWA). The
// modal stays mounted while OAuth navigates away because `login()`
// triggers a `window.location.href` change; if the user cancels, they
// come back to the same modal state.
const GoogleAuthButton = ({ onGoogleSignIn }) => (
    <button
        type="button"
        onClick={onGoogleSignIn}
        className="w-full h-12 rounded-full bg-white text-[#3C4043] font-semibold text-[15px] flex items-center justify-center gap-3 border border-[#DADCE0] shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-[#F8F9FA] active:scale-[0.98] transition-transform"
        style={{ fontFamily: "'Google Sans', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
        data-testid="email-auth-google-btn"
    >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.6-2.2 3.4v2.8h3.6c2.1-2 3.2-4.9 3.2-8.3z" />
            <path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.8c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8C4 20.6 7.7 23 12 23z" />
            <path fill="#FBBC04" d="M6 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.9H2.3C1.5 8.5 1 10.2 1 12s.5 3.5 1.3 5.1L6 14.3z" />
            <path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.1 1.6l3.1-3.1C17.4 2.1 14.9 1 12 1 7.7 1 4 3.4 2.3 6.9L6 9.7c.9-2.5 3.2-4.3 6-4.3z" />
        </svg>
        Continue with Google
    </button>
);

const OrDivider = () => (
    <div className="flex items-center gap-3 py-1" aria-hidden="true">
        <div className="flex-1 h-px bg-zinc-200" />
        <span className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-mono">or</span>
        <div className="flex-1 h-px bg-zinc-200" />
    </div>
);

// FieldIcon - input with a leading 16px icon. Kept tiny because we
// repeat it 6 times across the forms; saves ~80 lines of duplication.
const FieldIcon = ({ icon, children }) => (
    <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">{icon}</span>
        {children}
    </div>
);

const ErrorText = ({ message }) => (
    <div
        className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
        data-testid="email-auth-error"
        role="alert"
    >
        {message}
    </div>
);

export default EmailAuthSheet;